import { consoleEvents } from '@pb/utils'
import { batch } from 'redux-act'

import {
  CHECKOUT_ERROR_CODES,
  CHECKOUT_TRACKING_ERROR_TYPES,
  CHECKOUT_TRACKING_SECTIONS,
  GIFT_CARD_ERROR_MESSAGES,
  NON_TECHNICAL_GIFT_CARD_ERROR_CODES,
} from 'features/checkout/constants'
import {
  addGiftCardFail,
  addGiftCardStart,
  addGiftCardSuccess,
  fetchContextSuccess,
  removeGiftCardFail,
  removeGiftCardStart,
  removeGiftCardSuccess,
  setError,
} from 'features/checkout/duck/actions'
import { normalizeContext } from 'features/checkout/duck/normalize'
import checkoutSelectors from 'features/checkout/duck/selectors'
import { deleteGiftCard, postAddGiftCard } from 'features/checkout/requests'
import { setOrderTotals } from 'features/orderTotals/duck/actions'
import getErrorData from 'helpers/getErrorData'
import { ThunkResult } from 'redux/types'

import { trackError } from './track'

export const operationAddGiftCard =
  (formData): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(addGiftCardStart())
    try {
      const state = getState()
      const paymentMethods = checkoutSelectors.getAvailablePaymentMethods(state)
      const context = await postAddGiftCard(formData)
      // TODO: Refactor in COWEB-2433. Updating paymentModesList with old state as V2 don't return payment modes list
      context.payment.paymentModeList = paymentMethods

      const normalizedContext = normalizeContext(context)
      dispatch(batch(fetchContextSuccess(normalizedContext), addGiftCardSuccess()))
      dispatch(setOrderTotals(context.orderTotals))
    } catch (error) {
      consoleEvents.error('Failed to add gift card', error)
      const { status, code: responseErrorCode, message } = getErrorData(error)
      const errorMessage = GIFT_CARD_ERROR_MESSAGES[responseErrorCode]
      if (status === 400) {
        const isNonTechnicalErrorCode =
          NON_TECHNICAL_GIFT_CARD_ERROR_CODES.includes(responseErrorCode)
        if (isNonTechnicalErrorCode) {
          dispatch(
            setError({
              code: CHECKOUT_ERROR_CODES.nonTechnicalGiftCardError,
              message: errorMessage,
            }),
          )
        }
      }
      if (
        !NON_TECHNICAL_GIFT_CARD_ERROR_CODES.includes(responseErrorCode) &&
        responseErrorCode &&
        message
      ) {
        dispatch(
          trackError({
            section: CHECKOUT_TRACKING_SECTIONS.payment,
            message,
            id: responseErrorCode,
            errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
          }),
        )
      }
      dispatch(addGiftCardFail())
    }
  }

export const operationRemoveGiftCard =
  (cardNumber: string): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState()
    const originalCardsList = checkoutSelectors.getGiftCards(state)
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
    dispatch(removeGiftCardStart(cardNumber))
    try {
      const paymentMethods = checkoutSelectors.getAvailablePaymentMethods(state)
      const context = await deleteGiftCard(cardNumber)
      // TODO: Refactor in COWEB-2433. Updating paymentModesList with old state as V2 don't return payment modes list
      context.payment.paymentModeList = paymentMethods

      const normalizedContext = normalizeContext(context)
      dispatch(batch(fetchContextSuccess(normalizedContext), removeGiftCardSuccess()))
      dispatch(setOrderTotals(context.orderTotals))
    } catch (error) {
      const { code: responseErrorCode, message } = getErrorData(error)
      const errorMessage = GIFT_CARD_ERROR_MESSAGES[responseErrorCode] || message

      if (responseErrorCode && errorMessage) {
        dispatch(
          trackError({
            section: CHECKOUT_TRACKING_SECTIONS.payment,
            message: errorMessage,
            id: responseErrorCode,
            errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
          }),
        )
      }
      consoleEvents.error('Failed to remove gift card', error)
      dispatch(removeGiftCardFail(originalCardsList))
    }
  }
